import React, { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { ReactComponent as CloseIcon } from 'assets/icons/cross.svg'
import { courseMapStore } from 'shared/stores/courseMapStore'
import { researchSessionStore } from 'shared/stores/researchSessionStore'
import { removeEmojiPrefix } from 'shared/utils/removeEmojiPrefix'
import {
	getCourseMapCategoryLabel,
	getCourseMapSubCategories,
} from 'shared/utils/courseMapLookup'
import AiToolErrorBanner from '../AiToolContainer/components/AiToolErrorBanner/AiToolErrorBanner'
import s from './styles/ResearchCategoryPicker.module.scss'

/**
 * Category → SubCategory picker. User commits via researchSessionStore.commitProfile
 * (Profile SSOT only — never creates Journey).
 * Empty personal sessions are seeded with first catalog entries in useResearchPage
 * (not inside this picker).
 */
const ResearchCategoryPicker = observer(function ResearchCategoryPicker({
	isOpen,
	onClose,
	initialStep = 'category',
	onCommitted,
	courseMapError,
	courseMapLoading,
	onRetryCourseMap,
}) {
	const courseMap = courseMapStore.courseMap
	const draft = researchSessionStore.draftDefaults
	const storedCategory = draft?.category || ''
	const storedSubCategory = draft?.subCategory || ''

	const levels = useMemo(() => Object.keys(courseMap || {}), [courseMap])

	const [step, setStep] = useState(initialStep)
	const [activeCategory, setActiveCategory] = useState(storedCategory)
	const [activeSubCategory, setActiveSubCategory] = useState(storedSubCategory)

	const subCategories = useMemo(
		() => getCourseMapSubCategories(courseMap, activeCategory),
		[courseMap, activeCategory],
	)

	useEffect(() => {
		if (!isOpen) return
		setStep(initialStep === 'subCategory' && storedCategory ? 'subCategory' : 'category')
		setActiveCategory(storedCategory)
		setActiveSubCategory(storedSubCategory)
	}, [isOpen, initialStep, storedCategory, storedSubCategory])

	if (!isOpen) return null

	const onSelectCategoryItem = labelWithEmoji => {
		setActiveCategory(removeEmojiPrefix(labelWithEmoji))
	}

	const onApplyCategory = () => {
		if (!activeCategory) return
		const nextCategory = removeEmojiPrefix(activeCategory) || activeCategory
		const categoryChanged = nextCategory !== storedCategory

		if (categoryChanged) {
			researchSessionStore.commitProfile({
				category: nextCategory,
				subCategory: '',
				career: '',
				countries: [],
				country: '',
				course: '',
			})
			setActiveSubCategory('')
			onCommitted?.({ categoryChanged: true, subCategoryChanged: true })
		}
		setStep('subCategory')
	}

	const onSelectSubCategoryItem = labelWithEmoji => {
		setActiveSubCategory(removeEmojiPrefix(labelWithEmoji))
	}

	const onApplySubCategory = () => {
		if (!activeCategory || !activeSubCategory) return
		const nextCategory = removeEmojiPrefix(activeCategory) || activeCategory
		const nextSubCategory =
			removeEmojiPrefix(activeSubCategory) || activeSubCategory
		const categoryChanged = nextCategory !== storedCategory
		const subCategoryChanged = nextSubCategory !== storedSubCategory

		researchSessionStore.commitProfile({
			category: nextCategory,
			subCategory: nextSubCategory,
			...(categoryChanged || subCategoryChanged
				? { career: '', countries: [], country: '', course: '' }
				: {}),
		})
		if (categoryChanged || subCategoryChanged) {
			onCommitted?.({ categoryChanged, subCategoryChanged })
		}
		onClose()
	}

	const onClearCategory = () => {
		setActiveCategory('')
		setActiveSubCategory('')
		researchSessionStore.commitProfile({
			category: '',
			subCategory: '',
			career: '',
			countries: [],
			country: '',
			course: '',
		})
		onCommitted?.({ categoryChanged: true, subCategoryChanged: true })
		setStep('category')
	}

	const onBackdropClose = () => {
		onClose()
	}

	const categoryBadgeLabel = activeCategory
		? getCourseMapCategoryLabel(courseMap, activeCategory)
		: ''

	return (
		<div className={s.overlay} role="dialog" aria-modal="true">
			<button type="button" className={s.backdrop} aria-label="Close" onClick={onBackdropClose} />
			<div className={s.panel}>
				<header className={s.panelHeader}>
					<div className={s.panelHeaderCopy}>
						<strong className={s.panelHeaderTitle}>
							{step === 'category' ? 'Select Category' : 'Select Sub-category'}
						</strong>
						{step === 'subCategory' && categoryBadgeLabel ? (
							<button type="button" className={s.badge} onClick={onClearCategory}>
								{categoryBadgeLabel}
								<span className={s.badgeClose} aria-hidden>×</span>
							</button>
						) : null}
					</div>
					<button type="button" className={s.close} onClick={onBackdropClose} aria-label="Close">
						<CloseIcon className={s.closeIcon} />
					</button>
				</header>

				{courseMapError ? (
					<AiToolErrorBanner
						message="We couldn't load categories. Please try again later."
						onRetry={onRetryCourseMap}
						retrying={courseMapLoading}
					/>
				) : null}

				{courseMapLoading && levels.length === 0 && !courseMapError ? (
					<div className={s.empty}>Loading categories…</div>
				) : null}

				{step === 'category' ? (
					<div className={s.list}>
						{levels.map(level => {
							const selected =
								activeCategory &&
								removeEmojiPrefix(level) === removeEmojiPrefix(activeCategory)
							return (
								<button
									key={level}
									type="button"
									className={`${s.item} ${selected ? s.itemActive : ''}`}
									onClick={() => onSelectCategoryItem(level)}
								>
									{level}
								</button>
							)
						})}
					</div>
				) : (
					<div className={s.list}>
						{subCategories.length === 0 ? (
							<div className={s.empty}>No sub-categories for this category.</div>
						) : (
							subCategories.map(item => {
								const selected =
									activeSubCategory &&
									removeEmojiPrefix(item) === removeEmojiPrefix(activeSubCategory)
								return (
									<button
										key={item}
										type="button"
										className={`${s.item} ${selected ? s.itemActive : ''}`}
										onClick={() => onSelectSubCategoryItem(item)}
									>
										{item}
									</button>
								)
							})
						)}
					</div>
				)}

				<footer className={s.panelFooter}>
					<button type="button" className={s.secondary} onClick={onBackdropClose}>
						Cancel
					</button>
					{step === 'category' ? (
						<button
							type="button"
							className={s.primary}
							disabled={!activeCategory}
							onClick={onApplyCategory}
						>
							Continue
						</button>
					) : (
						<button
							type="button"
							className={s.primary}
							disabled={!activeSubCategory}
							onClick={onApplySubCategory}
						>
							Apply
						</button>
					)}
				</footer>
			</div>
		</div>
	)
})

export default ResearchCategoryPicker
